import { useEffect, useState } from "react";
import { Users, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { resolveFileUrl } from "@/components/FileDropzone";
import FileDropzone from "@/components/FileDropzone";
import { toast } from "sonner";

function CreateGroupModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", description: "", cover_url: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/groups", form);
      onCreated(data);
      onClose();
      setForm({ name: "", description: "", cover_url: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFBF7] border-2 border-black w-full max-w-lg p-6"
        data-testid="group-form"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="font-display font-black uppercase text-2xl">New group</div>
          <button type="button" onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4">
          <input
            required
            placeholder="Group name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="group-name"
            className="w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm"
          />
          <textarea
            placeholder="Description"
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            data-testid="group-desc"
            className="w-full px-3 py-2 bg-white border-2 border-black font-mono text-sm resize-none"
          />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest mb-2">Cover image</div>
            <FileDropzone
              value={form.cover_url}
              onChange={(u) => setForm({ ...form, cover_url: u })}
              accept="image/*"
              label="Upload cover"
              testId="group-cover"
              aspect="aspect-[16/9]"
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="group-submit"
          className="mt-6 w-full py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest btn-brutal disabled:opacity-50"
        >
          {busy ? "..." : "Create group"}
        </button>
      </form>
    </div>
  );
}

function GroupDetail({ group, onClose, onUpdate }) {
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!group) return;
    api.get(`/groups/${group.id}/posts`).then((r) => setPosts(r.data));
  }, [group]);

  const join = async () => {
    await api.post(`/groups/${group.id}/join`);
    onUpdate({ ...group, joined: true, member_count: group.member_count + 1 });
  };

  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/posts`, { text });
      setPosts((p) => [data, ...p]);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!group) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="group-detail"
        className="bg-[#FDFBF7] border-2 border-black w-full max-w-2xl max-h-[85vh] flex flex-col"
      >
        <div className="relative h-40 bg-[#0A0A0A]">
          {group.cover_url && (
            <img src={resolveFileUrl(group.cover_url)} alt="" className="w-full h-full object-cover" />
          )}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 bg-white border-2 border-black"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 border-b-2 border-black">
          <div className="font-display font-black text-2xl uppercase tracking-tighter">{group.name}</div>
          <div className="mt-1 font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60">
            {group.member_count} members
          </div>
          {group.description && <p className="mt-3 text-sm">{group.description}</p>}
          {!group.joined && (
            <button
              onClick={join}
              data-testid="group-join"
              className="mt-4 px-4 py-2 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal"
            >
              Join group
            </button>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {group.joined && (
            <div className="mb-4 flex gap-2">
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="group-post-input"
                placeholder="Post to the group..."
                className="flex-1 px-3 py-2 bg-white border-2 border-black font-mono text-sm"
              />
              <button
                onClick={post}
                disabled={busy || !text.trim()}
                data-testid="group-post-submit"
                className="px-4 bg-[#F7931A] border-2 border-black text-sm font-bold uppercase disabled:opacity-50"
              >
                Post
              </button>
            </div>
          )}
          {posts.length === 0 ? (
            <div className="font-mono text-xs uppercase tracking-widest text-[#0A0A0A]/60 py-8 text-center">
              No posts in this group yet.
            </div>
          ) : (
            posts.map((p) => (
              <div key={p.id} className="py-4 border-b border-black/10">
                <div className="font-display font-bold text-sm">{p.author?.name}</div>
                <div className="text-sm mt-1">{p.text}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);

  const load = async () => {
    const { data } = await api.get("/groups");
    setGroups(data);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <main className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-[#F7931A]" />
            <h1 className="font-display font-black text-4xl lg:text-5xl uppercase tracking-tighter">Groups</h1>
          </div>
          <p className="mt-2 text-sm text-[#0A0A0A]/60 font-mono uppercase">
            Bitcoin communities.
          </p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="btn-new-group"
          className="inline-flex items-center gap-2 px-4 py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal"
        >
          <Plus className="w-4 h-4" />
          New group
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="border-2 border-black bg-white p-12 text-center font-mono text-sm text-[#0A0A0A]/60 uppercase tracking-widest">
          No groups yet. Start one.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <button
              key={g.id}
              onClick={() => setActive(g)}
              data-testid={`group-${g.id}`}
              className="text-left border-2 border-black bg-white card-brutal overflow-hidden"
            >
              <div className="aspect-[16/9] bg-[#F7931A]/10 border-b-2 border-black overflow-hidden">
                {g.cover_url ? (
                  <img src={resolveFileUrl(g.cover_url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center font-display font-black text-4xl text-[#F7931A]">
                    ₿
                  </div>
                )}
              </div>
              <div className="p-4">
                <div className="font-display font-bold text-lg">{g.name}</div>
                <div className="font-mono text-xs uppercase text-[#0A0A0A]/60 mt-1">
                  {g.member_count} members
                </div>
                {g.description && (
                  <div className="text-sm mt-2 text-[#0A0A0A]/80 line-clamp-2">{g.description}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      <CreateGroupModal open={open} onClose={() => setOpen(false)} onCreated={(g) => setGroups((gs) => [g, ...gs])} />
      <GroupDetail
        group={active}
        onClose={() => {
          setActive(null);
          load();
        }}
        onUpdate={(g) => {
          setActive(g);
          setGroups((gs) => gs.map((x) => (x.id === g.id ? g : x)));
        }}
      />
    </main>
  );
}
